/**
 * Per-leg cross-track-distance editor. While this is open the leg's corridor is
 * drawn on the chart with draggable magenta handles, so the XTD can be set by
 * eye as well as typed. Where a nearby charted danger is found, a recommended
 * value is offered (a coarse proximity heuristic on the training cells, not a
 * swept-path analysis, so it's a starting suggestion to sanity-check).
 */
import { useEffect, useState } from 'react';
import { useShellStore } from '@/store/shell';
import { useRoutingStore } from '@/store/routing';
import { effectiveLegXtd } from '@/routing';
import { Sheet } from './Sheet';
import { GlossaryTerm } from '@/help/GlossaryTerm';

export function LegXtdSheet() {
  const editingLegXtd = useShellStore((s) => s.editingLegXtd);
  const setEditingLegXtd = useShellStore((s) => s.setEditingLegXtd);
  const rec = useShellStore((s) => s.legXtdRecommendation);
  const activeSheet = useShellStore((s) => s.activeSheet);
  const openSheet = useShellStore((s) => s.openSheet);
  const showToast = useShellStore((s) => s.showToast);
  const routes = useRoutingStore((s) => s.routes);
  const setLegXtd = useRoutingStore((s) => s.setLegXtd);
  const setLegNote = useRoutingStore((s) => s.setLegNote);

  const route = editingLegXtd ? routes.find((r) => r.id === editingLegXtd.routeId) : undefined;
  const legIndex =
    route && editingLegXtd ? route.waypoints.findIndex((w) => w.id === editingLegXtd.fromId) : -1;
  const current = route && editingLegXtd ? effectiveLegXtd(route, editingLegXtd.fromId) : null;

  const [xtd, setXtd] = useState('0.20');
  const [note, setNote] = useState('');
  const rid = editingLegXtd?.routeId;
  const fid = editingLegXtd?.fromId;
  useEffect(() => {
    if (current != null) setXtd(String(Number(current.toFixed(3))));
  }, [rid, fid, current]);
  // Load this leg's saved note when the sheet opens on a different leg.
  useEffect(() => {
    setNote(fid && route ? route.legNotes?.[fid] ?? '' : '');
  }, [rid, fid, route]);

  // Closing the sheet by any route (backdrop, save) drops the edit highlight.
  useEffect(() => {
    if (activeSheet !== 'legxtd' && editingLegXtd) setEditingLegXtd(null);
  }, [activeSheet, editingLegXtd, setEditingLegXtd]);

  if (!route || !editingLegXtd || legIndex < 0) {
    return <Sheet name="legxtd" title="Leg XTD" />;
  }

  function save() {
    const x = parseFloat(xtd);
    if (Number.isFinite(x) && x > 0) setLegXtd(editingLegXtd!.routeId, editingLegXtd!.fromId, x);
    setLegNote(editingLegXtd!.routeId, editingLegXtd!.fromId, note.trim() || null);
    setEditingLegXtd(null);
    openSheet('route');
    showToast('Leg XTD set');
  }

  function clearLeg() {
    setLegXtd(editingLegXtd!.routeId, editingLegXtd!.fromId, null);
    setLegNote(editingLegXtd!.routeId, editingLegXtd!.fromId, note.trim() || null);
    setEditingLegXtd(null);
    openSheet('route');
    showToast('Leg uses the route default');
  }

  return (
    <Sheet
      name="legxtd"
      title={`Leg ${legIndex + 1} cross-track`}
      sub="Set how far off the leg triggers the alarm. Drag the magenta handles on the chart to set it by eye, or type it."
    >
      <label className="acp-fieldlabel"><GlossaryTerm termKey="xtd">Cross-track distance</GlossaryTerm> (nm)</label>
      <input
        className="acp-text-input"
        value={xtd}
        onChange={(e) => setXtd(e.target.value)}
        placeholder="0.20"
        inputMode="decimal"
      />
      {rec != null ? (
        <div className="acp-buildnote">
          Suggested from nearby charted dangers: {rec.toFixed(2)} nm.
          <button
            type="button"
            className="acp-linkbtn"
            onClick={() => setXtd(String(Number(rec.toFixed(3))))}
          >
            Use suggestion
          </button>
        </div>
      ) : (
        <p className="acp-empty">
          No nearby charted danger found to base a suggestion on, so set this from seamanship.
        </p>
      )}
      <label className="acp-fieldlabel">Leg notes</label>
      <textarea
        className="acp-note-input"
        value={note}
        onChange={(e) => setNote(e.target.value)}
        rows={2}
        placeholder="Note for this leg — e.g. keep north of the wreck, strong ebb across here"
      />
      <div className="acp-btn-row">
        <button type="button" className="acp-btn" onClick={clearLeg}>
          Use route default
        </button>
        <button type="button" className="acp-btn primary" onClick={save}>
          Save
        </button>
      </div>
    </Sheet>
  );
}
